import { useCallback, useEffect, useState } from "react";
import { isAxiosError } from "axios";
import {
  addPendingMarker,
  incrementRetries,
  listPendingMarkers,
  removePendingMarker,
  type PendingMarker,
} from "../db/offlineDb";
import { createMarkerRequest } from "../api/markersApi";
import { useOnlineStatus } from "./useOnlineStatus";
import type { MarkerCategory } from "../types/domain.types";

export interface EnqueueInput {
  planId: string;
  qrCode: string;
  category?: MarkerCategory;
  coordX: number;
  coordY: number;
  comment?: string;
  userId: string;
  userEmail: string;
}

/**
 * Gestiona la cola IDB de marcadores pendientes y los sincroniza cuando
 * hay conectividad. Idempotente: reintenta individualmente cada marcador.
 */
export function useOfflineQueue(planId?: string) {
  const online = useOnlineStatus();
  const [pending, setPending] = useState<PendingMarker[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const list = await listPendingMarkers(planId);
    setPending(list);
  }, [planId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enqueue = useCallback(
    async (input: EnqueueInput): Promise<PendingMarker> => {
      const marker: PendingMarker = {
        localId:
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        planId: input.planId,
        qrCode: input.qrCode,
        category: input.category ?? null,
        coordX: input.coordX,
        coordY: input.coordY,
        comment: input.comment ?? null,
        createdAtClient: new Date().toISOString(),
        userId: input.userId,
        userEmail: input.userEmail,
        retries: 0,
      };
      await addPendingMarker(marker);
      await refresh();
      return marker;
    },
    [refresh],
  );

  const drain = useCallback(async (): Promise<{
    synced: number;
    failed: number;
  }> => {
    if (syncing) return { synced: 0, failed: 0 };
    setSyncing(true);
    setSyncError(null);

    let synced = 0;
    let failed = 0;
    try {
      const queue = await listPendingMarkers();
      for (const m of queue) {
        try {
          await createMarkerRequest({
            planId: m.planId,
            qrCode: m.qrCode,
            category: m.category ?? undefined,
            coordX: m.coordX,
            coordY: m.coordY,
            comment: m.comment ?? undefined,
          });
          await removePendingMarker(m.localId);
          synced++;
        } catch (err) {
          // 4xx (validación, permisos) => descartamos para no loopear.
          // 5xx / red => reintento futuro.
          const status = isAxiosError(err) ? err.response?.status : undefined;
          if (status && status >= 400 && status < 500) {
            await removePendingMarker(m.localId);
            failed++;
          } else {
            await incrementRetries(m.localId);
            failed++;
          }
        }
      }
    } finally {
      setSyncing(false);
      await refresh();
    }
    return { synced, failed };
  }, [syncing, refresh]);

  // Auto-drain al recuperar conexión
  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    (async () => {
      const q = await listPendingMarkers();
      if (cancelled || q.length === 0) return;
      try {
        await drain();
      } catch (e) {
        setSyncError(e instanceof Error ? e.message : "Error al sincronizar");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [online, drain]);

  return {
    pending,
    syncing,
    syncError,
    enqueue,
    drain,
    refresh,
    online,
  };
}
